import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTodos, Todo } from '../../crm-todos/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface CompletedTodosProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const CompletedTodos: React.FC<CompletedTodosProps> = ({ limit = 5, embedded, settings }) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { todos, isLoading } = useTodos();

  const items = React.useMemo(() => {
    const arr = Array.isArray(todos) ? todos : [];
    return arr
      .filter((t: Todo) => t.todoCompleted)
      .sort((a: Todo, b: Todo) => {
        const da = new Date(a.todoCompletedAt || a.todoUpdatedAt || 0).getTime();
        const db = new Date(b.todoCompletedAt || b.todoUpdatedAt || 0).getTime();
        return db - da;
      })
      .slice(0, effectiveLimit);
  }, [todos, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<Todo>
      title="Completed Todos"
      viewAllPath="/todos"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No completed todos"
      getItemKey={(t) => t.todoId}
      embedded={embedded}
      renderItem={(todo) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/todos/${todo.todoId}`)}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/todos/${todo.todoId}`)}
          className={`flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0`}
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
              {todo.todoDescr || 'Todo'}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {todo.client?.clientName && `${todo.client.clientName} · `}
              {todo.todoCompletedAt
                ? new Date(todo.todoCompletedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default CompletedTodos;
